import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Check,
  X,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  Layers,
  Clock,
  Search,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DeadlineBadge = ({ deadline }) => {
  if (!deadline) return null;
  const hoursLeft = Math.max(0, Math.round((new Date(deadline) - Date.now()) / 3600000));
  const urgent = hoursLeft < 12;
  const label = hoursLeft === 0 ? 'Overdue' : hoursLeft < 24 ? `${hoursLeft}h left` : `${Math.round(hoursLeft / 24)}d left`;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
      urgent ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
    }`}>
      <Clock className="w-3 h-3" />
      {label}
    </span>
  );
};

const ClientApprovals = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [changeComment, setChangeComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectTargetId, setRejectTargetId] = useState(null);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Bulk Selection State
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkComment, setBulkComment] = useState('');

  // View Mode: 'swipe' or 'grid'
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/posts`);
      const pendingPosts = response.data.posts.filter(
        p => p.approval_stage === 'client_review' || p.status === 'pending_approval' || p.status === 'client_review'
      );
      setPosts(pendingPosts);
    } catch (error) {
      toast.error('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = posts.filter(p =>
    !searchQuery.trim() || p.copy?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentPost = filteredPosts[currentIndex];

  const handleApprove = async (postId = null) => {
    const targetPostId = postId || currentPost?.id;
    if (!targetPostId) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/posts/${targetPostId}/approve`);
      toast.success('Post approved!');
      if (!postId) {
        removeCurrentPost();
      } else {
        fetchPosts();
      }
    } catch (error) {
      toast.error('Failed to approve post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!currentPost || !changeComment.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/posts/${currentPost.id}/request-changes?comment=${encodeURIComponent(changeComment)}`);
      toast.success('Changes requested');
      setChangeDialogOpen(false);
      setChangeComment('');
      removeCurrentPost();
    } catch (error) {
      toast.error('Failed to request changes');
    } finally {
      setSubmitting(false);
    }
  };

  // Open reject dialog — captures which post to reject
  const handleReject = (postId = null) => {
    setRejectTargetId(postId || currentPost?.id || null);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const confirmReject = async () => {
    if (!rejectTargetId) return;
    setSubmitting(true);
    try {
      const params = rejectReason.trim() ? `?reason=${encodeURIComponent(rejectReason)}` : '';
      await axios.post(`${API_URL}/posts/${rejectTargetId}/reject${params}`);
      toast.success('Post rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
      if (!rejectTargetId || rejectTargetId === currentPost?.id) {
        removeCurrentPost();
      } else {
        fetchPosts();
      }
    } catch (error) {
      toast.error('Failed to reject post');
    } finally {
      setSubmitting(false);
    }
  };

  const removeCurrentPost = () => {
    const newPosts = filteredPosts.filter((_, idx) => idx !== currentIndex);
    setPosts(prev => prev.filter(p => p.id !== filteredPosts[currentIndex]?.id));
    if (currentIndex >= newPosts.length) {
      setCurrentIndex(Math.max(0, newPosts.length - 1));
    }
  };

  const togglePostSelection = (postId) => {
    if (selectedPosts.includes(postId)) {
      setSelectedPosts(selectedPosts.filter(id => id !== postId));
    } else {
      setSelectedPosts([...selectedPosts, postId]);
    }
  };

  const selectAllPosts = () => {
    if (selectedPosts.length === filteredPosts.length) {
      setSelectedPosts([]);
    } else {
      setSelectedPosts(filteredPosts.map(p => p.id));
    }
  };

  const handleBulkAction = async () => {
    if (selectedPosts.length === 0 || !bulkAction) return;
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/posts/bulk-action`, {
        post_ids: selectedPosts,
        action: bulkAction,
        comment: bulkComment || undefined
      });

      const results = response.data.results || [];
      const successCount = results.filter(r => r.success).length;
      const failedCount = results.filter(r => !r.success).length;

      if (successCount > 0) {
        toast.success(`Successfully processed ${successCount} post(s)`);
      }
      if (failedCount > 0) {
        toast.warning(`Failed to process ${failedCount} post(s)`);
      }

      setBulkActionDialogOpen(false);
      setBulkAction('');
      setBulkComment('');
      setSelectedPosts([]);
      setSelectionMode(false);
      fetchPosts();
    } catch (error) {
      toast.error('Failed to perform bulk action');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      draft: 'bg-slate-100 text-slate-700',
      internal_review: 'bg-[#d8e5b8] text-[#3d4f22]',
      client_review: 'bg-amber-100 text-amber-700',
      pending_approval: 'bg-amber-100 text-amber-700',
      approved: 'bg-[#d8e5b8] text-[#3d4f22]',
      rejected: 'bg-red-100 text-red-700',
      change_requested: 'bg-orange-100 text-orange-700',
      scheduled: 'bg-purple-100 text-purple-700',
      posted: 'bg-green-100 text-green-700'
    };
    return classes[status] || 'bg-slate-100 text-slate-700';
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3d4f22]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-approvals">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
            Pending Approvals
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            {posts.length} post{posts.length !== 1 ? 's' : ''} awaiting your review
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentIndex(0); }}
              placeholder="Search posts..."
              className="pl-9 w-48"
              data-testid="search-posts"
            />
          </div>

          {/* Bulk Selection Toggle */}
          {posts.length > 0 && (
            <Button
              variant={selectionMode ? "default" : "outline"}
              onClick={() => {
                setSelectionMode(!selectionMode);
                setSelectedPosts([]);
              }}
              className={selectionMode ? "bg-[#3d4f22] hover:bg-[#3d4f22]" : ""}
              data-testid="toggle-selection-mode"
            >
              <Layers className="w-4 h-4 mr-2" />
              {selectionMode ? 'Exit Selection' : 'Bulk Select'}
            </Button>
          )}

          {/* View Mode Toggle */}
          <div className="flex rounded-lg border border-slate-200 dark:border-slate-700">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 text-sm ${viewMode === 'grid' ? 'bg-[#d8e5b8] text-[#3d4f22]' : 'text-slate-600'}`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('swipe')}
              className={`px-3 py-2 text-sm ${viewMode === 'swipe' ? 'bg-[#d8e5b8] text-[#3d4f22]' : 'text-slate-600'}`}
            >
              Swipe
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectionMode && selectedPosts.length > 0 && (
        <Card className="bg-[#f0f4e8] border-[#b8cf88] dark:bg-[#556B2F]/10 dark:border-[#3d4f22]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={selectedPosts.length === filteredPosts.length}
                  onCheckedChange={selectAllPosts}
                  data-testid="select-all-checkbox"
                />
                <span className="text-sm font-medium text-[#3d4f22] dark:text-[#b8cf88]">
                  {selectedPosts.length} of {filteredPosts.length} selected
                </span>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setBulkAction('reject');
                    setBulkActionDialogOpen(true);
                  }}
                  className="text-red-600 hover:bg-red-50"
                  data-testid="bulk-reject-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Reject All
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setBulkAction('request_changes');
                    setBulkActionDialogOpen(true);
                  }}
                  className="text-orange-600 hover:bg-orange-50"
                  data-testid="bulk-changes-btn"
                >
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Request Changes
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setBulkAction('approve');
                    setBulkActionDialogOpen(true);
                  }}
                  className="bg-[#3d4f22] hover:bg-[#3d4f22] text-white"
                  data-testid="bulk-approve-btn"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Approve All
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {filteredPosts.length === 0 ? (
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="py-16 text-center">
            <CheckCircle2 className="w-16 h-16 text-[#556B2F] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              {posts.length === 0 ? 'All Caught Up!' : 'No Results'}
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              {posts.length === 0
                ? 'You have no posts pending approval. Check back later!'
                : 'No posts match your search. Try a different keyword.'}
            </p>
          </CardContent>
        </Card>
      ) : viewMode === 'swipe' ? (
        /* Swipe Mode View */
        <div className="flex flex-col items-center space-y-6">
          <AnimatePresence mode="wait">
            {currentPost && (
              <motion.div
                key={currentPost.id}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                className="w-full max-w-2xl"
              >
                <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 overflow-hidden">
                  {/* Post Header */}
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={getStatusBadgeClass(currentPost.approval_stage || currentPost.status)}>
                          {formatStatus(currentPost.approval_stage || currentPost.status)}
                        </Badge>
                        <Badge variant="outline" className="capitalize">
                          {currentPost.post_type?.replace(/_/g, ' ')}
                        </Badge>
                        <DeadlineBadge deadline={currentPost.review_deadline} />
                      </div>
                      <span className="text-sm text-slate-500">
                        {currentIndex + 1} of {filteredPosts.length}
                      </span>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Post Content */}
                    <div
                      className="prose prose-slate max-w-none whitespace-pre-wrap text-slate-700 dark:text-slate-300 leading-relaxed bg-slate-50 dark:bg-slate-800 p-4 rounded-lg max-h-64 overflow-y-auto cursor-pointer"
                      onClick={() => navigate(`/client/posts/${currentPost.id}`)}
                    >
                      {currentPost.copy}
                    </div>

                    {/* Images */}
                    {currentPost.image_urls?.length > 0 && (
                      <div className="grid grid-cols-2 gap-2">
                        {currentPost.image_urls.slice(0, 2).map((url, idx) => (
                          <img
                            key={idx}
                            src={url}
                            alt=""
                            className="rounded-lg w-full h-32 object-cover bg-slate-100"
                            referrerPolicy="no-referrer"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        ))}
                      </div>
                    )}

                    {/* Scheduled Date */}
                    {currentPost.scheduled_date && (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Calendar className="w-4 h-4" />
                        Scheduled: {format(new Date(currentPost.scheduled_date), 'PPP')}
                      </div>
                    )}
                  </CardContent>

                  {/* Action Buttons */}
                  <div className="border-t border-slate-200 dark:border-slate-700 p-4">
                    <div className="flex items-center justify-center gap-4">
                      <Button
                        size="lg"
                        variant="outline"
                        onClick={() => handleReject()}
                        disabled={submitting}
                        className="w-14 h-14 rounded-full text-red-600 border-red-200 hover:bg-red-50"
                        data-testid="swipe-reject-btn"
                      >
                        <X className="w-6 h-6" />
                      </Button>

                      <Button
                        size="lg"
                        variant="outline"
                        onClick={() => setChangeDialogOpen(true)}
                        disabled={submitting}
                        className="w-14 h-14 rounded-full text-orange-600 border-orange-200 hover:bg-orange-50"
                        data-testid="swipe-changes-btn"
                      >
                        <MessageSquare className="w-6 h-6" />
                      </Button>

                      <Button
                        size="lg"
                        onClick={() => handleApprove()}
                        disabled={submitting}
                        className="w-14 h-14 rounded-full bg-[#3d4f22] hover:bg-[#3d4f22] text-white"
                        data-testid="swipe-approve-btn"
                      >
                        <Check className="w-6 h-6" />
                      </Button>
                    </div>

                    <p className="text-center text-xs text-slate-500 mt-3">
                      Click on content to view full details
                    </p>
                  </div>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
              disabled={currentIndex === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-slate-500">
              {currentIndex + 1} / {filteredPosts.length}
            </span>
            <Button
              variant="outline"
              onClick={() => setCurrentIndex(Math.min(filteredPosts.length - 1, currentIndex + 1))}
              disabled={currentIndex === filteredPosts.length - 1}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      ) : (
        /* Grid Mode View */
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPosts.map((post) => (
            <Card
              key={post.id}
              className={`bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-[#91a85e] transition-all ${
                selectedPosts.includes(post.id) ? 'ring-2 ring-[#556B2F]' : ''
              }`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  {selectionMode && (
                    <Checkbox
                      checked={selectedPosts.includes(post.id)}
                      onCheckedChange={() => togglePostSelection(post.id)}
                      className="mr-2"
                      data-testid={`checkbox-${post.id}`}
                    />
                  )}
                  <div className="flex items-center gap-2 flex-wrap flex-1">
                    <Badge className={`text-xs ${getStatusBadgeClass(post.approval_stage || post.status)}`}>
                      {formatStatus(post.approval_stage || post.status)}
                    </Badge>
                    <DeadlineBadge deadline={post.review_deadline} />
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-3">
                {/* Post Preview */}
                <div
                  className="text-sm text-slate-700 dark:text-slate-300 line-clamp-4 cursor-pointer hover:text-[#3d4f22] transition-colors"
                  onClick={() => navigate(`/client/posts/${post.id}`)}
                >
                  {post.copy}
                </div>

                {/* Thumbnail */}
                {post.image_urls?.length > 0 && (
                  <img
                    src={post.image_urls[0]}
                    alt=""
                    className="w-full h-24 object-cover rounded-lg bg-slate-100"
                    referrerPolicy="no-referrer"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                )}

                {/* Scheduled Date */}
                {post.scheduled_date && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {format(new Date(post.scheduled_date), 'MMM d')}
                  </p>
                )}

                {/* Quick Actions (non-selection mode) */}
                {!selectionMode && (
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReject(post.id)}
                      className="flex-1 text-red-600 hover:bg-red-50"
                      data-testid={`reject-${post.id}`}
                    >
                      <X className="w-3 h-3 mr-1" />
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleApprove(post.id)}
                      className="flex-1 bg-[#3d4f22] hover:bg-[#3d4f22] text-white"
                      data-testid={`approve-${post.id}`}
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Approve
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Request Changes Dialog (Swipe Mode) */}
      <Dialog open={changeDialogOpen} onOpenChange={setChangeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Request Changes
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              What changes would you like the agency to make?
            </p>
            <Textarea
              value={changeComment}
              onChange={(e) => setChangeComment(e.target.value)}
              placeholder="Describe the changes you'd like..."
              rows={4}
              data-testid="change-comment-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleRequestChanges}
              disabled={!changeComment.trim() || submitting}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {submitting ? 'Submitting...' : 'Request Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <X className="w-5 h-5 text-red-500" />
              Reject Post
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Optionally provide a reason for rejection so the agency can improve the post.
            </p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (optional)..."
              rows={3}
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={confirmReject}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-reject-btn"
            >
              {submitting ? 'Rejecting...' : 'Reject Post'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={bulkActionDialogOpen} onOpenChange={setBulkActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {bulkAction === 'approve' && <CheckCircle2 className="w-5 h-5 text-[#556B2F]" />}
              {bulkAction === 'reject' && <X className="w-5 h-5 text-red-500" />}
              {bulkAction === 'request_changes' && <AlertTriangle className="w-5 h-5 text-orange-500" />}
              {bulkAction === 'approve' && 'Bulk Approve'}
              {bulkAction === 'reject' && 'Bulk Reject'}
              {bulkAction === 'request_changes' && 'Bulk Request Changes'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              You are about to {bulkAction?.replace(/_/g, ' ')} <strong>{selectedPosts.length}</strong> post{selectedPosts.length > 1 ? 's' : ''}.
            </p>

            {(bulkAction === 'reject' || bulkAction === 'request_changes') && (
              <div>
                <Textarea
                  value={bulkComment}
                  onChange={(e) => setBulkComment(e.target.value)}
                  placeholder={bulkAction === 'reject' ? 'Reason for rejection (optional)...' : 'Describe the changes needed (optional)...'}
                  rows={3}
                  data-testid="bulk-comment-input"
                />
              </div>
            )}

            <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Selected Posts:</p>
              <ScrollArea className="max-h-32">
                <ul className="text-xs text-slate-500 space-y-1">
                  {selectedPosts.map(id => {
                    const post = filteredPosts.find(p => p.id === id);
                    return (
                      <li key={id} className="truncate">
                        • {post?.copy?.substring(0, 60)}...
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkActionDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleBulkAction}
              disabled={submitting}
              className={
                bulkAction === 'approve' ? 'bg-[#3d4f22] hover:bg-[#3d4f22]' :
                bulkAction === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                'bg-orange-600 hover:bg-orange-700'
              }
              data-testid="confirm-bulk-action-btn"
            >
              {submitting ? 'Processing...' : `Confirm ${bulkAction?.replace(/_/g, ' ')}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientApprovals;
